/**
 * How to render a span's input/output value, decided by its mime type.
 *
 * Spans carry `input.mime_type` / `output.mime_type` per the OpenInference convention, stored as
 * `input_mime_type` / `output_mime_type` and returned by the v2 API. A value is only JSON when
 * the span says so.
 *
 * Why this lives in one place: InputTab and OutputTab each decided this for themselves, drifted
 * apart, and produced different renderings of the same value. OutputTab guessed from content
 * shape and looked fine; InputTab parsed unconditionally and reported valid plain text as
 * "Invalid JSON - showing raw value" on nearly every trace. One rule, one module.
 *
 * Default is TEXT, deliberately. If the mime is missing the worst case is JSON shown as plain
 * text, which is merely ugly. The reverse default tells customers their data is broken when it
 * isn't.
 */

export const MIME_JSON = 'application/json';
export const MIME_TEXT = 'text/plain';

/**
 * Should this value be rendered as a JSON tree?
 * Only when the span explicitly declares JSON. Anything else, including a missing mime, is text.
 *
 * @param {string|null|undefined} mimeType - span.input_mime_type or span.output_mime_type
 * @returns {boolean}
 */
export function isJsonMime(mimeType) {
  return typeof mimeType === 'string' && mimeType.trim().toLowerCase() === MIME_JSON;
}

/**
 * Did the span claim JSON but hand us something unparseable?
 *
 * This is the only genuinely broken case, and it is an instrumentation defect worth surfacing.
 * "Not JSON" on its own is not an error.
 *
 * @param {string|null|undefined} mimeType
 * @param {*} value
 * @returns {boolean}
 */
export function isMalformedJson(mimeType, value) {
  if (!isJsonMime(mimeType) || typeof value !== 'string' || !value.trim()) return false;
  try {
    JSON.parse(value);
    return false;
  } catch {
    return true;
  }
}
